import { cn } from '@/lib/utils';
import type { EvaluacionCumplimiento } from '@adelante/shared';
import { AlertTriangle, CheckCircle2, Circle, XCircle } from 'lucide-react';

/**
 * Indicador visual del cumplimiento de resistencia de una muestra/ensayo.
 *
 * Estados:
 *   - cumple    → verde     ≥ 95% del teórico
 *   - marginal  → amarillo  85-95% del teórico (zona de alerta)
 *   - incumple  → rojo      < 85% del teórico
 *   - sin_dato  → gris      todavía no hay mediciones
 */

const ESTILOS: Record<
  EvaluacionCumplimiento,
  { label: string; clase: string; Icon: typeof CheckCircle2 }
> = {
  cumple: {
    label: 'Cumple',
    clase: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    Icon: CheckCircle2,
  },
  marginal: {
    label: 'Marginal',
    clase: 'border-amber-300 bg-amber-50 text-amber-900',
    Icon: AlertTriangle,
  },
  incumple: {
    label: 'No cumple',
    clase: 'border-red-300 bg-red-50 text-red-900',
    Icon: XCircle,
  },
  sin_dato: {
    label: 'Sin datos',
    clase: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    Icon: Circle,
  },
};

interface Props {
  evaluacion: EvaluacionCumplimiento;
  /** Solo icono, sin texto. Útil en columnas estrechas. */
  compacto?: boolean;
  className?: string;
}

export function BadgeCumplimiento({ evaluacion, compacto = false, className }: Props) {
  const e = ESTILOS[evaluacion];

  if (compacto) {
    return (
      <span
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full',
          e.clase,
          className,
        )}
        title={e.label}
      >
        <e.Icon className="h-3.5 w-3.5" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        e.clase,
        className,
      )}
      title={e.label}
    >
      <e.Icon className="h-3 w-3" />
      {e.label}
    </span>
  );
}
